/**
 * Performance notes:
 *
 * - The main issue is every Pixi frame causing "Update Layer Tree" in Chrome, which takes forever
 *   (40ms) with a large Slate.js document where we use "position:absolute" for the words.
 *
 *   Remember: we have less than 16ms per frame.
 *
 *   - can we prevent "update layer tree" from being triggered?
 *   - can we debug "update layer tree" somehow?
 *       https://developer.mozilla.org/en-US/docs/Mozilla/Performance/Profiling_with_the_Built-in_Profiler
 *       https://github.com/GoogleChrome/lighthouse/pull/2030/files
 *
 * - React-Pixi is pretty fast, but 10ms is not a lot, and 60fps is a lot of calls. We might be able to
 *   reduce CPU usage by forgoing react-pixi, or only using it for some parts (it allows intermixing).
 *
 * - When not playing or moving, we still render at 60fps.
 *    - we might switch to "raf=false" mode then
 *    - we just render on stage update, with props, as in scrolling?
 *
 * - Consider these libs:
 *    - https://davidfig.github.io/pixi-scrollbox/
 *    - https://github.com/davidfig/pixi-cull
 */


/**
 * There are different ways the UI can work. In principal, there are two approaches;
 *
 * 1) When panning the viewport, the play position remains the same - the viewport scrolls.
 * 2) When panning the viewport, we really change the play position - the viewport scrolls as a result of
 *    the play head remaining in a stable position.
 *
 * The Youtube Subtitler uses approach (2).
 *
 * The consequence is that it is not possible to show a subtitle in the timeline which does not fit into the
 * same view as the play position. While scrolling through the captions editor, we have to adjust the play
 * position along with it.
 *
 * An alternative would be two separates behaviours for "while playing" and "while paused".
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import webAudioBuilder from "waveform-data/webaudio";
import {WaveformShape} from "./WaveFormShape";
import {getRecommendedWaveformDisplayGain} from "./drawWaveform";
import {PlayHead} from "./PlayHead";
import {Scale} from "./coordinates";
import {CaptionOverlay} from "./CaptionOverlay";
import {Stage} from '@pixi/react'
import Measure from 'react-measure';
import {string2hex} from "./utils";
import {MediaTrack} from "../../models/MediaTrack";
import {LoadingStatus} from "./LoadingStatus";
import {WaveformData} from "waveform-data";
import {ICaptionsEditor} from "../../models/ICaptionsEditor";
import {UIState, UserInterfaceAPI} from "../MainScreen";
import {findPathOfVocableIdOrNull, getSelectedLineGroupIndex, getSelectedWordInline} from "../../models/Captions/slateApi";
import {TimeScale} from "./TimeScale";
import {Editor, Transforms} from 'slate';
import {ReactEditor} from 'slate-react';
import {PlayerAPI} from "languagetool-player/src/ui/Video";
import {Viewport} from "./Viewport";
import {CaptionTrackRoot} from "../../models/Captions/types";
import {getWordTimingViewport} from "./wordTiming";


// The react typings always include "null" in "current", which seems wrong.
interface RefObject<T> {
  readonly current: T;
}


type Props = {
  // This is where we get the waveform from.
  source: MediaTrack,
  // This is the current captions we render on top of the wave form.
  captions: CaptionTrackRoot|null,
  // The API to change the wave form.
  captionsEditor: ICaptionsEditor|null,
  uiAPI: UserInterfaceAPI,
  uiState: UIState

  playerAPI: PlayerAPI,

  height?: any
};


const stageOptions = {
  antialias: false,
  resolution: 2,
  backgroundColor: string2hex('#fdf7f7')
}

export function Timeline(props: Props) {
  const [dimensions, setDimensions] = useState<{width: number, height: number}|null>(null);
  const [stageStyle, setStageStyle] = useState<{width?: number, height?: number}>({});

  const handleResize = useCallback((contentRect: any) => {
    setDimensions(contentRect.bounds);

    // NB: When passing "resolution: 2" to Pixi, the only thing it does is *2 EVERY VALUE, including
    // the width/height of the stage. So basically, we really want to then scale it down with CSS to
    // half the size so we have a true x2 resolution.
    // We cache the proper css style object in state to not cause unnecessary re-renders.
    setStageStyle(contentRect.bounds ? {
      width: contentRect.bounds.width,
      height: contentRect.bounds.height,
    } : {});
  }, [setDimensions, setStageStyle]);

  let stage: any|null = null;

  if (dimensions) {
    stage = (
      <Stage
        width={dimensions.width}
        height={dimensions.height}
        style={stageStyle}
        options={stageOptions}

        // If "raf" is enabled, this is ignored, and always treated as "false" - since we
        // re-render anyway in the background at 60fps. But if this mode is used, Pixi is
        // re-rendered in Stage.componentDidUpdate, regardless of any actual changes in
        // properties. Note further, that what never works is to update the state of any
        // Pixi child object directly.
        //
        // Note that with this mode, and thus without "raf", state updates of children
        // will not cause a refresh, because react-pixi will not repaint the stage if a
        // child component updates, but relies on the ticker to do it.
        renderOnComponentChange={false}
        // This sets up a Pixi background timer. We do not really need this, I think, and
        // it seems to increase the idle CPU usage to 25%.
        raf={true}
      >
        <Contents
          width={dimensions.width}
          height={dimensions.height}
          source={props.source}
          captions={props.captions}
          captionsEditor={props.captionsEditor}
          uiAPI={props.uiAPI}
          uiState={props.uiState}
          playerAPI={props.playerAPI}
        />
      </Stage>
    );
  }

  return (
    <Measure
      bounds
      onResize={handleResize}
    >
      {({ measureRef }) => (
        <div ref={measureRef} style={{width: '100%', height: props.height}}>
          {stage}
        </div>
      )}
    </Measure>
  );
}


type ContentProps = {
  width: number,
  height: number,

  // This is where we get the waveform from.
  source: MediaTrack,
  // This is the current captions we render on top of the wave form.
  captions: CaptionTrackRoot|null,
  // The API to change the wave form.
  captionsEditor: ICaptionsEditor|null,
  uiAPI: UserInterfaceAPI,
  uiState: UIState
  playerAPI: PlayerAPI,
};


function useRefState<T>(defaultValue: T): [T, any, RefObject<T>] {
  const [state, setState] = useState(defaultValue);
  const ref = useRef(state);
  useEffect(() => {
    ref.current = state;
  })
  return [state, setState, ref];
}


function useCopyToRef<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}


function useLoadWaveForm(source: MediaTrack): [WaveformData|null, boolean] {
  const [waveFormData, setWaveFormData] = useState<WaveformData|null>(null);
  const [waveFormLoading, setWaveFormLoading] = useState(true);

  const audioContext = useMemo(() => {
    // @ts-ignore: Safari still requires the prefix.
    const AudioContextClass: typeof AudioContext = window.AudioContext || window.webkitAudioContext;
    return new AudioContextClass();
  }, []);

  useEffect(() => {
    if (source.type !== 'url') {
      // For now, we do not have a wave form for those kinds of media files.
      setWaveFormLoading(false);
    }

    else {
      const p = loadWaveformFromUrl(source.url!, audioContext);
      p.then((data: WaveformData) => {
        setWaveFormLoading(false);
        setWaveFormData(data as WaveformData);
      }).catch(err => {
        console.info('Failed to load the wave form data', err);
        setWaveFormLoading(false);
      });
    }
  }, [source]);

  return [waveFormData, waveFormLoading];
}


function useScale(waveFormData: WaveformData|null, width: number): [Scale, number] {
  const [userZoom, setUserZoom] = useState(1);

  const scale = useMemo(() => {
    // If we do not have a waveform yet, make it 40 pixels per second
    return new Scale(40 * userZoom);
  }, [userZoom]);

  const secondsVisible = useMemo(() => {
    return scale.pixelsToTime(width);
  }, [scale, width]);

  return [scale, secondsVisible];
}


function ensureVisible(time: number, secondsVisible: number, setOffsetSeconds: any, offsetSeconds: number) {
  // Logic: Always in the middle. Not clear how that affects the other UI interactions.
  // const desiredMiddleSecond = offsetSeconds + secondsVisible / 2;
  // if (time > desiredMiddleSecond) {
  //   offsetSeconds = Math.max(0, time - (secondsVisible / 2));
  // }
}


function useOffsetSeconds(playerAPI: PlayerAPI, secondsVisible: number): [RefObject<number>, any] {
  const [offsetSeconds, setOffsetSeconds] = useState(0);

  const setOffsetSecondsSafe = useCallback((time: number) => {
    if (!Number.isFinite(time)) {
      throw new Error('offset must be a number')
    }
    const duration = playerAPI.getState().duration;

    const safeOffset = Math.min(Math.max(0, time), duration !== null ? duration - secondsVisible : Infinity);
    setOffsetSeconds(safeOffset);
  }, [setOffsetSeconds, playerAPI, secondsVisible]);

  const lastOffsetSeconds = useRef(offsetSeconds);
  lastOffsetSeconds.current = offsetSeconds;

  return [lastOffsetSeconds, setOffsetSecondsSafe];
}


/**
 * Whenever the play position changes, update the viewport to make sure the playhead is
 * visible. This has two effects:
 *
 * - While playing, the playhead will remain visible. We implement it such that it remains
 *   in a stable position, and we move the viewport, always.
 *
 * - When seeking to a new location, the viewport will be adjusted to show the play head.
 *
 * Note that this used to be implemented as a "function of the play position" - it forced
 * the viewport on every frame to be "correct". However, for now, this is not what we want,
 * since while playback is being paused, we want to allow the user to scroll the viewport
 * away from the play position. Thus, the code here only executes on a play position "change",
 * not on every frame.
 * */
function useMakeOffsetFollowPlayPosition(
  playerAPI: PlayerAPI,
  secondsVisible: number,
  offsetSeconds: RefObject<number>,
  setOffsetSeconds: any,
  enabled: boolean,
) {
  const seekInProgress = useRef<number|null>(null);

  // This stores the last known play head position
  const [playHeadPosSeconds, setPlayHeadPosSeconds, playHeadPosSecondsRef] = useRefState(0);

  // instead of executing the follow logic on every frame, should it instead subscribe to bacon?
  const onTime = useCallback((time: number) =>
  {
    // Since this is triggered via requestAnimationFrame, not via the playerAPI.time$ callback, we will
    // keep getting an old value after a seek instruction for a couple of frames, until it takes old.
    // We use a hack here to try to hold any changes until the seek is complete.
    // We are lucky this seems to work!
    if (seekInProgress.current) {
      if (seekInProgress.current === time) {
        // The seek is complete, now free the blocker.
        seekInProgress.current = null;
      }
      else {
        return;
      }
    }

    // Attempt to adjust the offset while keeping the play position the same. Note that this
    // will not work anymore when reaching the end of the timeline, but that is ok: the setOffsetSeconds()
    // function will clamp it to the end, and the play head, which we do not actually control via the state,
    // will render in the right place always.
    setOffsetSeconds(time - playHeadPosSecondsRef.current);
  }, [setOffsetSeconds, secondsVisible, offsetSeconds]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return playerAPI.time$.onValue(time => {
      onTime(time);
    });
  }, [enabled, onTime, playerAPI]);

  const seekWithPlayHead = useCallback((time: number) => {
    if (!enabled) {
      playerAPI.seekTo(time);
      return;
    }

    seekInProgress.current = time;
    setPlayHeadPosSeconds(time - offsetSeconds.current);
    playerAPI.seekTo(time);
  }, [enabled, playerAPI, setPlayHeadPosSeconds, offsetSeconds])


  return [playHeadPosSeconds, seekWithPlayHead];
}


function useOffsetDraggingEvents(offsetSeconds: RefObject<number>, setOffsetSeconds: any) {
  const startOffset = useRef(0);

  const onMoveStart = useCallback(() => {
    startOffset.current = offsetSeconds.current;
  }, [startOffset]);

  const onMoving = useCallback((offsetShift: number) => {
    let newOffset = startOffset.current + offsetShift;
    if (newOffset < 0) {
      newOffset = 0;
    }
    setOffsetSeconds(newOffset);
  }, [setOffsetSeconds, startOffset]);

  const onMoveDone = useCallback(() => {}, [])

  return {
    onMoving,
    onMoveDone,
    onMoveStart,
  }
}

/**
 * Returns event handlers for <Viewport> which have the affect of seeking the player when we scroll the
 * viewport, assuming the play head to remain in the position it is.
 */
function usePlayTimeDraggingEvents(offsetSeconds: RefObject<number>, setOffsetSeconds: any, playerAPI: PlayerAPI) {
  const startPosition = useRef<number>(0);
  const startOffset = useRef<number>(0);
  const wasPlaying = useRef(false);

  const onMoveStart = useCallback((position: number) => {
    startPosition.current = playerAPI.getCurrentTime();
    startOffset.current = offsetSeconds.current;
    wasPlaying.current = playerAPI.getState().isPlaying;
    playerAPI.pause();
  }, [playerAPI, startPosition])

  const onMoving = (offsetShift: number) => {
    if (startOffset.current! + offsetShift < 0) {
      offsetShift = -startOffset.current!;
    }

    playerAPI.seekTo(startPosition.current + offsetShift);
  }

  const onMoveDone = useCallback(() => {
    if (wasPlaying.current) {
      playerAPI.play();
    }
  }, [playerAPI, wasPlaying])

  return {
    onMoveStart,
    onMoving,
    onMoveDone,
  }
}


// Merge the dragging events for playing and not playing. This is kind of a hack because <Viewport> does not
// accept changes to the events.
function useDraggingEvents(playerAPI: PlayerAPI, playEvents: any, pauseEvents: any) {
  const wasPlayingOnMoveStart = useRef(false);

  const onMoveStart = useCallback((position: number) => {
    const isPlaying = playerAPI.getState().isPlaying;
    wasPlayingOnMoveStart.current = isPlaying;
    if (isPlaying) {
      playEvents.onMoveStart(position);
    }
    else {
      pauseEvents.onMoveStart(position);
    }
  }, [playerAPI, playEvents, pauseEvents, wasPlayingOnMoveStart])

  const onMoving = useCallback((position: number) => {
    if (wasPlayingOnMoveStart.current) {
      playEvents.onMoving(position);
    }
    else {
      pauseEvents.onMoving(position);
    }
  }, [playerAPI, playEvents, pauseEvents, wasPlayingOnMoveStart])

  const onMoveDone = useCallback(() => {
    if (wasPlayingOnMoveStart.current) {
      playEvents.onMoveDone();
    }
    else {
      pauseEvents.onMoveDone();
    }
  }, [playerAPI, playEvents, pauseEvents, wasPlayingOnMoveStart])

  return {
    onMoveStart,
    onMoving,
    onMoveDone,
  }
}


/**
 * Return a callback that will change the selected line in `captionsEditor` when invoked.
 *
 * This is used when a subtitle is selected in the timeline.
 */
function useSelectLineInCaptionEditor(captionsEditor: ICaptionsEditor|null) {
  return useCallback((lineIdx: number) => {
    if (!captionsEditor) {
      return null;
    }

    Transforms.select(captionsEditor.slateEditor, [lineIdx]);
  }, [captionsEditor]);
}


function useSelectWordInCaptionEditor(captionsEditor: ICaptionsEditor|null) {
  return useCallback((vocableId: string) => {
    if (!captionsEditor) {
      return null;
    }

    const path = findPathOfVocableIdOrNull(captionsEditor.committedValue, vocableId);
    if (!path) {
      return null;
    }

    Transforms.select(captionsEditor.slateEditor, Editor.range(captionsEditor.slateEditor, path));
    ReactEditor.focus(captionsEditor.slateEditor);
  }, [captionsEditor]);
}


function useOnTapWhichChangesPlayheadPosition(offsetSeconds: RefObject<number>, playHeadSeek: any) {
  return useCallback((newPosition: number) => {
    playHeadSeek(newPosition + offsetSeconds.current!);
  }, [offsetSeconds, playHeadSeek]);
}


function useLoopPlaybackWithinLine(
  playerAPI: PlayerAPI,
  line: {time: number, duration: number}|null,
  enabled: boolean,
) {
  const isAdjusting = useRef(false);

  useEffect(() => {
    if (!enabled || !line) {
      return;
    }

    isAdjusting.current = true;
    playerAPI.seekTo(line.time).finally(() => {
      isAdjusting.current = false;
      playerAPI.play();
    });
  }, [enabled, line?.duration, line?.time, playerAPI]);

  useEffect(() => {
    if (!enabled || !line) {
      return;
    }

    const lineEnd = line.time + line.duration;

    return playerAPI.time$.onValue(time => {
      if (isAdjusting.current) {
        return;
      }

      if (time < line.time || time >= lineEnd - 0.02) {
        const shouldResume = playerAPI.getState().isPlaying;
        isAdjusting.current = true;
        playerAPI.seekTo(line.time).finally(() => {
          isAdjusting.current = false;
          if (shouldResume) {
            playerAPI.play();
          }
        });
      }
    });
  }, [enabled, line?.duration, line?.time, playerAPI]);
}


function useChangeViewportToShowLineSelectedInEditor(
  captions: ICaptionsEditor|null,
  setOffsetSeconds: any,
  secondsVisible: number,
  offsetSeconds: RefObject<number>,
  enabled: boolean,
) {
  const selectedLineIdx = useMemo(() => {
    return captions ? getSelectedLineGroupIndex(captions.slateEditor) : null;
  }, [captions?.slateEditor.children, captions?.slateEditor.selection]);

  // We have to make sure we trigger our effect only if the selected line changes!
  const captionsRef = useCopyToRef(captions);

  useEffect(() => {
    if (!enabled || selectedLineIdx === null || !captionsRef.current) {
      return;
    }
    const {time} = captionsRef.current.committedValue[selectedLineIdx].data;
    if (time !== undefined && time !== null) {
      let newOffset = offsetSeconds.current;

      // Logic: Wrap around when we reach the end of the visible area.
      if (time > offsetSeconds.current + secondsVisible) {
        newOffset = time;
      }

      // Logic: If outside of view on the left, move to center.
      if (time < newOffset) {
        newOffset = Math.max(0, time - (secondsVisible / 2));
      }

      setOffsetSeconds(newOffset);
    }
  }, [captionsRef, enabled, selectedLineIdx, secondsVisible, setOffsetSeconds, offsetSeconds]);
}


export function Contents(props: ContentProps) {
  const [waveformData, iswWaveFormLoading] = useLoadWaveForm(props.source);
  const waveformDisplayGain = useMemo(() => {
    return waveformData ? getRecommendedWaveformDisplayGain(waveformData) : 1;
  }, [waveformData]);
  const [scale, secondsVisible] = useScale(waveformData, props.width);

  // This is the viewport position.
  const [offsetSeconds, setOffsetSeconds] = useOffsetSeconds(props.playerAPI, secondsVisible);

  const wordTimingLine = useMemo(() => {
    if (!props.captions || !props.uiState.wordTiming) {
      return null;
    }

    const lineIdx = props.uiState.wordTiming.lineIdx;
    const line = props.captions[lineIdx];
    const {time, duration} = line?.data || {};
    if (time === undefined || duration === undefined) {
      return null;
    }

    return {lineIdx, time, duration};
  }, [props.captions, props.uiState.wordTiming]);

  const wordTimingViewport = useMemo(() => {
    if (!wordTimingLine) {
      return null;
    }

    return getWordTimingViewport(wordTimingLine.time, wordTimingLine.duration);
  }, [wordTimingLine]);

  const selectedWordVocableId = useMemo(() => {
    if (!wordTimingLine || !props.captionsEditor) {
      return null;
    }

    const selectedWord = getSelectedWordInline(props.captionsEditor.slateEditor);
    if (!selectedWord) {
      return null;
    }

    const [word, path] = selectedWord;
    return path[0] === wordTimingLine.lineIdx ? word.key : null;
  }, [props.captionsEditor?.slateEditor.selection, props.captionsEditor, wordTimingLine]);

  const scaleToRender = useMemo(() => {
    if (!wordTimingViewport) {
      return scale;
    }

    return new Scale(props.width / Math.max(0.1, wordTimingViewport.end - wordTimingViewport.start));
  }, [props.width, scale, wordTimingViewport]);

  const offsetSecondsToRender = wordTimingViewport ? wordTimingViewport.start : offsetSeconds.current;
  const secondsVisibleToRender = wordTimingViewport
    ? (wordTimingViewport.end - wordTimingViewport.start)
    : secondsVisible;
  const offsetSecondsToRenderRef = useCopyToRef(offsetSecondsToRender);

  // There are a couple of ways the viewport and play position can change. We have to make some decisions.
  //
  // While playing, we follow, keeping the playhead stable, just moving the viewport.
  const [_, playHeadSeek] = useMakeOffsetFollowPlayPosition(
    props.playerAPI,
    secondsVisible,
    offsetSeconds,
    setOffsetSeconds,
    !wordTimingViewport,
  );

  const regularDraggingEvents = useDraggingEvents(
    props.playerAPI,
    // And if we drag the viewport while playing, the seek position changes, the playhead remains stable.
    usePlayTimeDraggingEvents(offsetSeconds, setOffsetSeconds, props.playerAPI),
    // But if we are paused, and we pan the viewport, then we scroll, and the play position may scroll out of view.
    useOffsetDraggingEvents(offsetSeconds, setOffsetSeconds)
  )

  const draggingEvents = useMemo(() => {
    if (wordTimingViewport) {
      return {
        onMoveStart: () => {},
        onMoving: () => {},
        onMoveDone: () => {},
      };
    }

    return regularDraggingEvents;
  }, [regularDraggingEvents, wordTimingViewport]);

  // And if we select a line in the editor, we scroll the viewport to make that line visible, but only if not playing.
  useChangeViewportToShowLineSelectedInEditor(
    props.captionsEditor,
    setOffsetSeconds,
    secondsVisible,
    offsetSeconds,
    !wordTimingViewport,
  );

  useLoopPlaybackWithinLine(props.playerAPI, wordTimingLine, !!wordTimingViewport);

  const handleTapWithoutWordTiming = useOnTapWhichChangesPlayheadPosition(offsetSecondsToRenderRef, playHeadSeek);
  const handleTap = useCallback((newPosition: number) => {
    if (wordTimingLine) {
      const lineStart = wordTimingLine.time;
      const lineEnd = wordTimingLine.time + wordTimingLine.duration;
      const desiredTime = Math.min(Math.max(offsetSecondsToRender + newPosition, lineStart), lineEnd);
      props.playerAPI.seekTo(desiredTime);
      return;
    }

    handleTapWithoutWordTiming(newPosition);
  }, [handleTapWithoutWordTiming, offsetSecondsToRender, props.playerAPI, wordTimingLine]);

  // If we select a line in the timeline, mirror that selection to the text editor.
  const handleLineSelect = useSelectLineInCaptionEditor(props.captionsEditor);
  const handleWordSelect = useSelectWordInCaptionEditor(props.captionsEditor);
  const handleLineDoubleClick = useCallback((lineIdx: number) => {
    handleLineSelect(lineIdx);
    props.uiAPI.enterWordTiming(lineIdx);
  }, [handleLineSelect, props.uiAPI]);

  const {width, height} = props;

  return <>
    <Viewport
      x={0}
      y={0}
      height={height}
      width={width}
      scale={scaleToRender}
      {...draggingEvents}
      onTap={handleTap}
    />
    {/* Renders too many texts on each update - we need to move them instead. */}
    <TimeScale
      scale={scaleToRender}
      offsetSeconds={offsetSecondsToRender}
      x={0}
      y={0}
      height={height}
      width={width}
    />

    {waveformData && (
      <>
        <WaveformShape
          data={waveformData}
          scale={scaleToRender}
          displayGain={waveformDisplayGain}
          offsetSeconds={offsetSecondsToRender}
          x={0}
          y={18}
          height={Math.max(1, height - 18)}
          width={width}
        />
      </>)}

    <LoadingStatus
      isLoading={iswWaveFormLoading}
      hasData={!!waveformData}
      stageHeight={height}
    />

    {(props.captions && props.captionsEditor) && <CaptionOverlay
      captions={props.captions}
      offsetSeconds={offsetSecondsToRender}
      scale={scaleToRender}
      maxSeconds={offsetSecondsToRender + secondsVisibleToRender}
      captionsEditor={props.captionsEditor}
      onLineSelect={handleLineSelect}
      onLineDoubleClick={handleLineDoubleClick}
      onWordSelect={handleWordSelect}
      selectedLine={wordTimingLine?.lineIdx ?? getSelectedLineGroupIndex(props.captionsEditor?.slateEditor, 0)!}
      selectedWordVocableId={selectedWordVocableId}
      wordTimingLineIdx={wordTimingLine?.lineIdx}
    />}

    <PlayHead
      height={height}
      scale={scaleToRender}
      offsetSeconds={offsetSecondsToRender}
      playerAPI={props.playerAPI}
      showText={false}
    />
  </>;
}

// TODO: Bring this back. Difficulty is Pixi not supporting the event, so we have to
// attach it at the State/canvas level - then either sent it down, or we attach it
// at the body in <Contents>, then filter.
// handleWheel = (e: any) => {
//   if (e.deltaMode !== 0 || e.deltaX === 0) {
//     return;
//   }
//
//   this.setOffsetSeconds(this.state.offsetSeconds + e.deltaX);
//   e.preventDefault();
// }
//



async function loadWaveformFromUrl(url: string, audioContext: AudioContext) {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();

  return await new Promise<WaveformData>((resolve, reject) => {
    webAudioBuilder(audioContext, buffer, (err: any, waveform: any) => {
      if (err) {
        reject(err);
        return;
      }

      resolve(waveform as WaveformData);
    });
  })
}